import { Head, Link, router } from "@inertiajs/react"
import { Box, Button, Card, Flex, Heading, Text } from "@radix-ui/themes"
import { Pencil, Send, Trash2 } from "lucide-react"
import { useEffect, useRef } from "react"
import { z } from "zod"

import LinkButton from "@/components/link-button"
import { MarkdownPreview } from "@/components/markdown-preview"
import StatusBadge from "@/components/status-badge"
import ProjectLayout from "@/layouts/project-layout"
import {
  editProjectNewsletterPath,
  projectDashboardPath,
  projectEmailPath,
  projectListPath,
  projectNewsletterPath,
  projectNewslettersPath,
  sendNewsletterProjectNewsletterPath,
} from "@/routes"
import { NewsletterSchema, ProjectSchema } from "@/schemas"

const PropsSchema = z.object({
  project: ProjectSchema,
  newsletter: NewsletterSchema,
  newsletter_email_recipients_count: z.number(),
  newsletter_push_recipients_count: z.number(),
  newsletter_email_style_css: z.string().nullable().optional(),
})

type Props = z.infer<typeof PropsSchema>

export default function NewsletterShow(props: Props) {
  const validatedProps = PropsSchema.parse(props)
  const {
    newsletter,
    project,
    newsletter_email_recipients_count,
    newsletter_push_recipients_count,
    newsletter_email_style_css,
  } = validatedProps
  const intervalRef = useRef<number | null>(null)
  const sendButtonLabel =
    newsletter.wants_send_email && newsletter.wants_send_push
      ? `Send email + push (${newsletter_email_recipients_count}/${newsletter_push_recipients_count})`
      : newsletter.wants_send_email
        ? `Send email to ${newsletter_email_recipients_count} customers`
        : `Send push to ${newsletter_push_recipients_count} customers`

  // Auto-refresh when newsletter is in sending state
  useEffect(() => {
    if (newsletter.status === "sending") {
      intervalRef.current = window.setInterval(() => {
        router.reload()
      }, 3000) // Refresh every 3 seconds

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
        }
      }
    }
  }, [newsletter.status])

  const handleSend = () => {
    let message = "Are you sure you want to send this newsletter?"
    if (newsletter.wants_send_email && newsletter.wants_send_push) {
      message =
        `Are you sure you want to send this newsletter? ` +
        `Email: ${newsletter_email_recipients_count} customers. ` +
        `Push: ${newsletter_push_recipients_count} customers.`
    } else if (newsletter.wants_send_email) {
      message = `Are you sure you want to send this newsletter to ${newsletter_email_recipients_count} customers?`
    } else if (newsletter.wants_send_push) {
      message = `Are you sure you want to send this push notification to ${newsletter_push_recipients_count} customers?`
    }

    if (confirm(message)) {
      router.post(sendNewsletterProjectNewsletterPath(project.slug, newsletter.id))
    }
  }

  const handleDelete = () => {
    if (confirm("Are you sure you want to delete this newsletter?")) {
      router.delete(projectNewsletterPath(project.slug, newsletter.id))
    }
  }

  return (
    <ProjectLayout
      projectId={project.slug}
      breadcrumbs={[
        { title: project.name, href: projectDashboardPath(project.slug) },
        { title: "Newsletters", href: projectNewslettersPath(project.slug) },
        { title: newsletter.name },
      ]}
    >
      <Head title={newsletter.name} />

      <Flex direction="column" gap="6" p="6">
        <Flex justify="between" align="start">
          <Flex direction="column" gap="2">
            <Flex align="center" gap="3">
              <Heading size="6" weight="medium">
                {newsletter.name}
              </Heading>
              <StatusBadge
                status={newsletter.status}
                color={
                  newsletter.status === "sent"
                    ? "green"
                    : newsletter.status === "sending"
                      ? "blue"
                      : newsletter.status === "failed"
                        ? "red"
                        : "gray"
                }
              />
            </Flex>
            {newsletter.sent_at && (
              <Text color="gray" size="2">
                Sent on {new Date(newsletter.sent_at).toLocaleString()}
              </Text>
            )}
            {newsletter.email_status === "sending" && newsletter.wants_send_email && (
              <Text color="gray" size="2">
                Email progress: {newsletter.count_sent} sent, {newsletter.count_failed} failed out
                of {newsletter.count_recipients} total (
                {newsletter.count_recipients > 0
                  ? Math.round((newsletter.count_sent / newsletter.count_recipients) * 100)
                  : 0}
                %)
              </Text>
            )}
            {newsletter.push_status === "sending" && newsletter.wants_send_push && (
              <Text color="gray" size="2">
                {newsletter.push_count_recipients > 0 ? (
                  <>
                    Push progress: {newsletter.push_count_sent} sent, {newsletter.push_count_failed}{" "}
                    failed out of {newsletter.push_count_recipients} total (
                    {Math.round(
                      (newsletter.push_count_sent / newsletter.push_count_recipients) * 100,
                    )}
                    %)
                  </>
                ) : (
                  <>Push: waiting for delivery to start...</>
                )}
              </Text>
            )}
            {newsletter.email_status === "sent" && newsletter.wants_send_email && (
              <Text color="green" size="2">
                Successfully sent to {newsletter.count_sent} out of {newsletter.count_recipients}{" "}
                recipients{newsletter.count_failed > 0 ? `, ${newsletter.count_failed} failed` : ""}
              </Text>
            )}
            {newsletter.email_status === "failed" && newsletter.wants_send_email && (
              <Text color="red" size="2">
                Failed to send to {newsletter.count_failed} out of {newsletter.count_recipients}{" "}
                recipients{newsletter.count_sent > 0 ? `, ${newsletter.count_sent} succeeded` : ""}
              </Text>
            )}
            {newsletter.wants_send_push && newsletter.push_status === "sent" && (
              <Text color="green" size="2">
                Push: Successfully sent to {newsletter.push_count_sent} out of{" "}
                {newsletter.push_count_recipients} devices
                {newsletter.push_count_failed > 0 ? `, ${newsletter.push_count_failed} failed` : ""}
              </Text>
            )}
            {newsletter.wants_send_push && newsletter.push_status === "failed" && (
              <Text color="red" size="2">
                Push: Failed — {newsletter.push_count_failed} out of{" "}
                {newsletter.push_count_recipients} devices failed
                {newsletter.push_count_sent > 0 ? `, ${newsletter.push_count_sent} succeeded` : ""}
              </Text>
            )}
          </Flex>

          <Flex gap="2">
            {newsletter.status === "draft" && (
              <LinkButton
                href={editProjectNewsletterPath(project.slug, newsletter.id)}
                variant="classic"
                color="gray"
              >
                <Pencil className="mr-1 h-4 w-4" />
                Edit
              </LinkButton>
            )}

            <Button onClick={handleDelete} variant="classic" color="red">
              <Trash2 className="mr-1 h-4 w-4" />
              Delete
            </Button>

            {newsletter.status === "draft" && (
              <Button onClick={handleSend} color="indigo" variant="classic">
                <Send className="mr-1 h-4 w-4" />
                {sendButtonLabel}
              </Button>
            )}
          </Flex>
        </Flex>

        <Flex direction="column" gap="4">
          <Box>
            <Text as="div" size="2" weight="bold" color="gray" mb="1">
              Email channel
            </Text>
            <Text size="3" color={newsletter.wants_send_email ? "green" : "gray"}>
              {newsletter.wants_send_email ? "Enabled" : "Disabled"}
            </Text>
          </Box>

          {newsletter.wants_send_email && (
            <>
              <Box>
                <Text as="div" size="2" weight="bold" color="gray" mb="1">
                  Email template
                </Text>

                {newsletter.email ? (
                  <Link href={projectEmailPath(project.slug, newsletter.email.id)}>
                    <Text size="3" color="blue">
                      {newsletter.email.subject ?? "Untitled Email"}
                    </Text>
                  </Link>
                ) : (
                  <Text size="3" color="gray">
                    No email template selected
                  </Text>
                )}
              </Box>

              <Box>
                <Text as="div" size="2" weight="bold" color="gray" mb="1">
                  Email send to
                </Text>

                {newsletter.email_segment ? (
                  <Text size="3" color="gray">
                    {newsletter.wants_exclude_email_segment
                      ? `All newsletter subscribers except segment: ${newsletter.email_segment.name}`
                      : `Segment: ${newsletter.email_segment.name}`}{" "}
                    ({newsletter_email_recipients_count} customers)
                  </Text>
                ) : newsletter.email_list ? (
                  <Link href={projectListPath(project.slug, newsletter.email_list.id)}>
                    <Text size="3" color="blue">
                      {newsletter.email_list.name} ({newsletter_email_recipients_count} customers)
                    </Text>
                  </Link>
                ) : (
                  <Text size="3" color="gray">
                    All newsletter subscribers ({newsletter_email_recipients_count} customers)
                  </Text>
                )}

                {newsletter.email_segment?.description && (
                  <Text color="gray" size="2" mt="1">
                    {newsletter.email_segment.description}
                  </Text>
                )}
              </Box>

              <Box>
                <Text as="div" size="2" weight="bold" color="gray" mb="1">
                  Email Subject
                </Text>
                <Text size="3">{newsletter.email?.subject ?? "No subject"}</Text>
              </Box>

              <Box>
                <Text as="div" size="2" weight="bold" color="gray" mb="1">
                  Email Preview
                </Text>
                {newsletter.email?.body ? (
                  <MarkdownPreview
                    value={newsletter.email.body}
                    css={newsletter_email_style_css ?? ""}
                  />
                ) : (
                  <Card variant="surface">
                    <Text size="2" color="gray">
                      No content
                    </Text>
                  </Card>
                )}
              </Box>
            </>
          )}

          <Box>
            <Text as="div" size="2" weight="bold" color="gray" mb="1">
              Push channel
            </Text>
            <Text size="3" color={newsletter.wants_send_push ? "green" : "gray"}>
              {newsletter.wants_send_push ? "Enabled" : "Disabled"}
            </Text>
          </Box>

          {newsletter.wants_send_push && (
            <>
              <Box>
                <Text as="div" size="2" weight="bold" color="gray" mb="1">
                  Push send to
                </Text>

                {newsletter.push_segment ? (
                  <Text size="3" color="gray">
                    {newsletter.wants_exclude_push_segment
                      ? `All newsletter subscribers except segment: ${newsletter.push_segment.name}`
                      : `Segment: ${newsletter.push_segment.name}`}{" "}
                    ({newsletter_push_recipients_count} customers)
                  </Text>
                ) : newsletter.push_list ? (
                  <Link href={projectListPath(project.slug, newsletter.push_list.id)}>
                    <Text size="3" color="blue">
                      {newsletter.push_list.name} ({newsletter_push_recipients_count} customers)
                    </Text>
                  </Link>
                ) : (
                  <Text size="3" color="gray">
                    All newsletter subscribers ({newsletter_push_recipients_count} customers)
                  </Text>
                )}

                {newsletter.push_segment?.description && (
                  <Text color="gray" size="2" mt="1">
                    {newsletter.push_segment.description}
                  </Text>
                )}
              </Box>

              <Box>
                <Text as="div" size="2" weight="bold" color="gray" mb="1">
                  Push Title
                </Text>
                <Text size="3">{newsletter.push_title || "No title"}</Text>
              </Box>

              <Box>
                <Text as="div" size="2" weight="bold" color="gray" mb="1">
                  Push Content
                </Text>
                <Text size="3">{newsletter.push_content || "No content"}</Text>
              </Box>
            </>
          )}

          <Box>
            <Text as="div" size="2" weight="bold" color="gray" mb="1">
              Created
            </Text>
            <Text size="3">{new Date(newsletter.created_at).toLocaleString()}</Text>
          </Box>
        </Flex>
      </Flex>
    </ProjectLayout>
  )
}
